import { useMemo } from "react";
import i18n, { getActivePseudoTag } from "i18n";

// Month names on the scale come from the browser's CLDR data via Intl, not
// from the translation bundle — dates are locale formatting, not translatable
// keys. Pseudo modes show plain English months: bracketing would suggest a
// missing key where none can exist.
const intlLocale = () => (getActivePseudoTag() ? "en" : i18n.language || "en");

const formatterCache = new Map();

// Returns a Gantt scale format function (cell start date -> label) rendering
// in the active UI locale. Formatters are cached per locale+options; the
// locale is read at format time so the scale follows the app language.
export const localisedDate = (options) => (date) => {
	const locale = intlLocale();
	const cacheKey = `${locale}|${JSON.stringify(options)}`;

	let formatter = formatterCache.get(cacheKey);
	if (!formatter) {
		try {
			formatter = new Intl.DateTimeFormat(locale, options);
		} catch {
			formatter = new Intl.DateTimeFormat("en", options);
		}
		formatterCache.set(cacheKey, formatter);
	}

	return formatter.format(date);
};

const MS_PER_DAY = 1000 * 60 * 60 * 24;

// Ordered from most zoomed out to most zoomed in.
//
// Changing level converts the cell width to the new unit and then clamps it to
// that level's bounds, so a bound tighter than the step between two units
// shows up as a jump mid-gesture. Each maxCellWidth below therefore leaves room
// for the level beneath it at its minimum: 24 hours to a day, 7 days to a week,
// ~4.3 weeks to a month.
const ZOOM_LEVELS = [
	// Level 0: Year view
	{
		minCellWidth: 40,
		maxCellWidth: 180,
		scales: [
			{ unit: "year", step: 1, format: "%Y" },
			{ unit: "month", step: 1, format: localisedDate({ month: "short" }) },
		],
	},
	// Level 1: Month view
	{
		minCellWidth: 40,
		maxCellWidth: 290,
		scales: [
			{
				unit: "month",
				step: 1,
				format: localisedDate({ month: "long", year: "numeric" }),
			},
			{ unit: "week", step: 1, format: "%W" },
		],
	},
	// Level 2: Week view
	{
		minCellWidth: 40,
		maxCellWidth: 500,
		scales: [
			{
				unit: "month",
				step: 1,
				format: localisedDate({ month: "long", year: "numeric" }),
			},
			{ unit: "day", step: 1, format: "%j" },
		],
	},
	// Level 3: Day and hour view
	{
		minCellWidth: 20,
		maxCellWidth: 230,
		scales: [
			{
				unit: "day",
				step: 1,
				format: localisedDate({
					weekday: "short",
					month: "short",
					day: "numeric",
				}),
			},
			{ unit: "hour", step: 1, format: "%H:%i" },
		],
	},
];

const DEFAULT_LEVEL = 1;
const MAX_LEVEL = ZOOM_LEVELS.length - 1;

export const levelForSpan = (spanInDays) => {
	if (spanInDays <= 3) return 3;
	if (spanInDays <= 14) return 2;
	if (spanInDays <= 90) return 1;
	return 0;
};

/**
 * Builds the zoom config for the span the chart actually covers.
 *
 * The level has to come from the same dates the chart is drawn from: deriving
 * it from the raw service records instead lets a single bad scheduled date
 * open the whole chart at a multi-year scale.
 */
export const useGanttZoom = (dateRange) => {
	const startTime = dateRange?.start ? dateRange.start.getTime() : null;
	const endTime = dateRange?.end ? dateRange.end.getTime() : null;

	return useMemo(() => {
		const level =
			startTime === null || endTime === null
				? DEFAULT_LEVEL
				: levelForSpan((endTime - startTime) / MS_PER_DAY);

		return {
			level: Math.min(Math.max(level, 0), MAX_LEVEL),
			levels: ZOOM_LEVELS,
		};
	}, [startTime, endTime]);
};
